import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ContentCard from '../components/ContentCard';
import { FaCheckCircle, FaHourglassHalf, FaCopy } from 'react-icons/fa'; // Добавляем иконки
import './ProfilePage.css';

// Упрощенное логирование
const log = (message) => {
  console.log(`[ProfilePage] ${message}`);
};

const ProfilePage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loyaltyEmojis, setLoyaltyEmojis] = useState({});
  const [loyaltyDescription, setLoyaltyDescription] = useState('');
  const [showLoyaltyTooltip, setShowLoyaltyTooltip] = useState(false);
  const [loyaltySettingsLoaded, setLoyaltySettingsLoaded] = useState(false);
  
  // Закрытие подсказки при клике вне её
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLoyaltyTooltip && !event.target.closest('.loyalty-level')) {
        setShowLoyaltyTooltip(false);
      }
    };
    
    if (showLoyaltyTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showLoyaltyTooltip]);
  
  // Получение информации о лояльности
  const fetchLoyaltyInfo = React.useCallback(async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      
      if (!telegramId && !token) {
        console.log('[ProfilePage] Нет данных для аутентификации при получении лояльности');
        return;
      }
      
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      if (telegramId) {
        headers['X-Telegram-ID'] = telegramId;
      }
      
      const response = await fetch('/api/users/loyalty-info', { headers });
      
      if (response.ok) {
        const data = await response.json();
        setLoyaltyInfo(data);
        console.log('[ProfilePage] Информация о лояльности получена:', data);
      } else {
        console.error('[ProfilePage] Ошибка получения лояльности:', response.status);
      }
    } catch (err) {
      console.error('Ошибка при получении информации о лояльности:', err);
    }
  }, [isAuthenticated, user]);

  // Получение настроек лояльности
  const fetchLoyaltySettings = async () => {
    try {
      const response = await fetch('/api/settings/public');
      
      if (response.ok) {
        const settingsData = await response.json();
        
        const emojis = {
          beginner: settingsData.loyalty_emoji_beginner || '🌱',
          student: settingsData.loyalty_emoji_student || '📚',
          expert: settingsData.loyalty_emoji_expert || '🎓',
          master: settingsData.loyalty_emoji_master || '👑'
        };
        
        setLoyaltyEmojis(emojis);
        setLoyaltyDescription(settingsData.loyalty_description || 'Это твой уровень. Действуй и ты незаметно вырастешь до мастера!');
        setLoyaltySettingsLoaded(true);
      }
    } catch (err) {
      console.error('Ошибка при получении настроек лояльности:', err);
    }
  };
  
  useEffect(() => {
    fetchLoyaltySettings();
  }, []);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchPurchases = async () => {
      setIsLoading(true);
      setError(null);

      try {
        log('Загрузка данных профиля...');
        const token = localStorage.getItem('auth_token');
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        
        if (!telegramId && !token) {
          throw new Error('Необходима авторизация для просмотра покупок');
        }
        
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (telegramId) headers['X-Telegram-ID'] = telegramId;
        
        // Используем новый эндпоинт
        const response = await fetch('/api/users/me/profile-items', { headers });
        
        if (!response.ok) {
          throw new Error(`Ошибка при получении элементов профиля: ${response.status}`);
        }
        
        const data = await response.json();
        log('Данные получены');
        
        // Новый эндпоинт должен возвращать массив напрямую
        if (Array.isArray(data)) {
          setPurchases(data);
        } else {
          log('Некорректный формат ответа от /api/users/me/profile-items');
          setPurchases([]);
        }
        log('Данные профиля обновлены');
      } catch (err) {
        log(`Ошибка при получении элементов профиля: ${err.message}`);
        console.error('Ошибка при получении элементов профиля:', err);
        setError(`Не удалось загрузить данные профиля: ${err.message}`);
        setPurchases([]);
      } finally {
        setIsLoading(false);
        log('Загрузка завершена');
      }
    };
    
    fetchPurchases();
    fetchLoyaltyInfo();
  }, [isAuthenticated, user, fetchLoyaltyInfo]);
  
  useEffect(() => {
    if (purchases.length > 0) {
      fetchLoyaltyInfo();
    }
  }, [purchases.length, fetchLoyaltyInfo]);

  const getLevelName = (level) => {
    const levelNames = {
      beginner: 'Новичок',
      student: 'Студент',
      expert: 'Эксперт',
      master: 'Мастер'
    };
    return levelNames[level] || 'Новичок';
  };
  
  if (!isAuthenticated) {
    return null;
  }
  
  return (
    <div className="profile-page content-with-navbar">
      <div className="tg-page-header"></div>
      <div className="profile-header">
        <div className="profile-info">
          <div className="user-details">
            <div className="user-header">
              <h1>{user?.firstName || 'Пользователь'} {user?.lastName || ''}</h1>
              
              {loyaltyInfo && loyaltySettingsLoaded && loyaltyEmojis[loyaltyInfo.loyaltyLevel] && (
              <div 
                className="loyalty-level"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLoyaltyTooltip(!showLoyaltyTooltip);
                }}
                style={{ cursor: 'pointer' }}
              >
                <span className="level-emoji">
                  {loyaltyEmojis[loyaltyInfo.loyaltyLevel]}
                </span>
                <span className="level-name">
                  {getLevelName(loyaltyInfo.loyaltyLevel)}
                </span>
                <span className="info-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M8 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="8" cy="5" r="0.5" fill="currentColor"/>
                  </svg>
                </span>
                
                {showLoyaltyTooltip && (
                  <div 
                    className="loyalty-tooltip"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <p>{loyaltyDescription}</p>
                  </div>
                )}
              </div>
            )}
            </div>
            <p className="username">@{user?.username || 'username'}</p>
          </div>
        </div>
      </div>
      
      <div className="purchases-section">
        <h2>Мои покупки</h2>
        
        <div className="content-list">
          {isLoading ? (
            <p>Загрузка ваших покупок...</p>
          ) : error ? (
            <div className="error-state">
              <p>Ошибка: {error}</p>
              <button onClick={() => window.location.reload()}>Повторить</button>
            </div>
          ) : purchases.length === 0 ? (
            <div className="empty-state">
              <p>У вас пока нет покупок</p>
            </div>
          ) : (
            purchases.map(item => {
              // Проверяем, является ли элемент подарком, купленным пользователем
              // и есть ли информация giftDetails
              if (item.isGiftPurchase && item.giftDetails) {
                return (
                  <div key={item._id || item.id} className="gift-purchase-card-profile">
                    <div className="gift-purchase-card-header">
                      <h4 className="gift-purchase-title">{item.title} (Подарок)</h4>
                      <div 
                        className={`gift-purchase-status ${item.giftDetails.isActivated ? 'activated' : 'not-activated'}`}
                        title={item.giftDetails.isActivated ? 'Активирован' : 'Не активирован'}
                      >
                        {item.giftDetails.isActivated ? <FaCheckCircle /> : <FaHourglassHalf />}
                      </div>
                    </div>
                    <div className="gift-purchase-details">
                      <p><strong>Дата покупки:</strong> {new Date(item.createdAt || item.giftDetails.createdAt).toLocaleString('ru-RU')}</p>
                      {item.giftDetails.isActivated ? (
                        <>
                          <p>
                            <strong>Активировал:</strong> 
                            {item.giftDetails.activatedBy?.username ? (
                              <a href={`https://t.me/${item.giftDetails.activatedBy.username}`} target="_blank" rel="noopener noreferrer">
                                @{item.giftDetails.activatedBy.username}
                              </a>
                            ) : (
                              item.giftDetails.activatedBy?.firstName || item.giftDetails.activatedBy?.telegramId || 'Неизвестно'
                            )}
                          </p>
                          {item.giftDetails.activatedAt && (
                            <p><strong>Дата активации:</strong> {new Date(item.giftDetails.activatedAt).toLocaleString('ru-RU')}</p>
                          )}
                        </>
                      ) : (
                        <p>Подарок еще не активирован.</p>
                      )}
                    </div>
                    {!item.giftDetails.isActivated && item.giftDetails.uniqueGiftId && (
                      <div className="gift-purchase-actions">
                        <button
                          className="admin-button" // Используем общий стиль кнопки, если подходит
                          onClick={() => {
                            const giftLink = `${window.location.origin}/gift/${item.giftDetails.uniqueGiftId}`;
                            navigator.clipboard.writeText(giftLink)
                              .then(() => alert('Ссылка на подарок скопирована!'))
                              .catch(err => alert('Не удалось скопировать ссылку: ' + err));
                          }}
                        >
                          <FaCopy style={{ marginRight: '5px' }} /> Копировать ссылку
                        </button>
                      </div>
                    )}
                  </div>
                );
              } else {
                // Это обычная покупка
                return (
                  <ContentCard 
                    key={item._id || item.id} 
                    content={item}
                    isPurchased={true} 
                  />
                );
              }
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
